import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import type { FeedEvent, FeedEventType, Achievement } from '../types/social';
import { Avatar } from './ui/Avatar';
import { Eyebrow } from './ui/Eyebrow';

interface Props {
  events: FeedEvent[];
  loading: boolean;
  catalog: Achievement[];
}

const ACCENT_BY_TYPE: Record<FeedEventType, string> = {
  team_complete: '#1B5E20',
  milestone: '#F2B90C',
  sticker_burst: '#2D5F8B',
  confed_complete: '#5B3F0F',
  wishlist_match: '#B0401E',
  achievement_earned: '#5B3F8B',
  streak_milestone: '#B0401E',
  trade_completed: '#1B5E20',
};

const EYEBROW_BY_TYPE: Record<FeedEventType, string> = {
  team_complete: 'Time completo',
  milestone: 'Marco',
  sticker_burst: 'Sequência',
  confed_complete: 'Confederação',
  wishlist_match: 'Pra você',
  achievement_earned: 'Conquista',
  streak_milestone: 'Streak',
  trade_completed: 'Troca',
};

function formatEventText(ev: FeedEvent, catalog: Achievement[]): string {
  const author = ev.author_name || 'Amigo';
  const p = ev.payload as Record<string, unknown>;
  switch (ev.event_type) {
    case 'team_complete': {
      const teamName = (p.team_name as string) || (p.team as string) || 'um time';
      return `${author} completou ${teamName}`;
    }
    case 'milestone': {
      const pct = (p.percent as number) ?? 0;
      return `${author} bateu ${pct}% do álbum`;
    }
    case 'sticker_burst': {
      const c = (p.count as number) ?? 0;
      return `${author} adicionou ${c} figurinhas`;
    }
    case 'confed_complete': {
      const c = (p.confed as string) ?? '';
      return `${author} fechou a ${c} inteira`;
    }
    case 'wishlist_match': {
      const code = (p.sticker_code as string) ?? '';
      return `${author} precisa da ${code}`;
    }
    case 'achievement_earned': {
      const id = (p.achievement_id as string) ?? '';
      const ach = catalog.find((a) => a.id === id);
      return `${author} ganhou "${ach?.name ?? 'medalha nova'}"`;
    }
    case 'streak_milestone': {
      const days = (p.days as number) ?? 0;
      return `${author} tá há ${days} dias seguidos`;
    }
    case 'trade_completed': {
      const given = (p.total_given as number) ?? 0;
      const received = (p.total_received as number) ?? 0;
      const total = given + received;
      return `${author} fechou uma troca de ${total} figurinha(s)`;
    }
    default:
      return `${author} fez algo legal`;
  }
}

export function FriendsFeed({ events, loading, catalog }: Props) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="px-4 mb-6">
        <Eyebrow className="mb-2.5">Novidades dos amigos</Eyebrow>
        <div className="flex gap-2.5 overflow-x-auto pb-1.5 -mx-4 px-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="shrink-0 rounded-2xl bg-white border border-ink-300 animate-pulse"
              style={{ width: 168, height: 108 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="mx-4 mb-6 rounded-2xl border border-dashed border-ink-300 bg-white px-5 py-6 text-center">
        <div className="flex items-center justify-center mb-2 text-green-700">
          <Sparkles size={22} />
        </div>
        <Eyebrow className="text-green-700 mb-1">Novidades dos amigos</Eyebrow>
        <p className="font-body text-small text-ink-500">
          Quando seus amigos completarem times ou ganharem medalhas, aparece aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="px-4 flex items-baseline justify-between mb-2.5">
        <Eyebrow>Novidades dos amigos</Eyebrow>
      </div>
      <div
        className="flex gap-2.5 overflow-x-auto px-4 pb-1.5 snap-x"
        style={{ scrollbarWidth: 'none' }}
      >
        {events.map((ev) => (
          <FeedCard key={ev.id} ev={ev} catalog={catalog} onOpen={() => navigate(`/profile/${ev.user_id}`)} />
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  ev: FeedEvent;
  catalog: Achievement[];
  onOpen: () => void;
}

function FeedCard({ ev, catalog, onOpen }: CardProps) {
  const accent = ACCENT_BY_TYPE[ev.event_type] ?? '#1B5E20';
  const text = formatEventText(ev, catalog);
  const eyebrow = EYEBROW_BY_TYPE[ev.event_type] ?? 'Novidade';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="shrink-0 snap-start text-left bg-white rounded-2xl shadow-xs btn-focus"
      style={{
        width: 168,
        minHeight: 108,
        borderLeft: `3px solid ${accent}`,
        border: '1px solid #E5DFD0',
        borderLeftWidth: 3,
        borderLeftColor: accent,
        padding: '12px 12px 10px 14px',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Avatar name={ev.author_name || 'Amigo'} size={28} />
        <Eyebrow color={accent} className="truncate">{eyebrow}</Eyebrow>
      </div>
      <p className="font-display font-semibold text-[13.5px] text-ink-900 leading-tight tracking-[-0.005em]">
        {text}
      </p>
    </button>
  );
}
